import { lang } from '@umu/ukit-lang';
/**
 * 文件选择，外部可通过传递 className 及 children 自定义
 */
import React, { useRef } from 'react';

import umuWebview from '@umu/umu-webview';
import { classSet } from '@umu/umu-dom';
import { services } from 'common';
const { Toast } = services;

import { getUploadResult, expandAccept, expandMediaAccept, IEncodeResult } from '../../services/uploader/uploader';
import { EMediaType, IUploadProgress } from '@umu/umu-types';

import './fileSelect.scss';

export interface IEndData {
    previewUrl: string;
    url: string;
    fileName: string;
    resourceId: string;
}

export type IFileSelectProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
    /** 业务资源类型 */
    mediaType: EMediaType;
    /** 登录用户身份 */
    teacherId: string;
    onlyPreview?: boolean;
    /** 处理完成一条数据前的回调，入参为资源 url */
    onPreview?: (objectUrl: string, file: File) => void;
    /** 处理完成一条数据后的回调，入参为资源 url */
    onData?: (previewUrl: string, url: string, fileName: string, resourceId: any) => void;
    /** 开始处理文件 */
    onStart?: () => void;
    onSelectReady?: (files: Array<{ file: File; previewUrl: string }>) => void;
    /** 文件全部上传完成 */
    onEnd?: (datas: IEndData[]) => void;
    /** 文件选择完成本地检测 */
    onCheck?: (files: File[]) => boolean | Promise<boolean>;
    /** 发送错误时 */
    onUploadError?: (errorMessage: Error) => void;
    progressHandler?: (fileName: string, progress: IUploadProgress) => void;
    multiple?: boolean;
    accept?: string;
    isValidate?: boolean; // 由于浏览器不兼容accept传参方式 添加该参数，更多层校验通过下面validate 方法进行校验
    validate?(files: FileList): boolean;
};

interface IPreviewResult {
    file: File;
    previewUrl: string;
}

export function FileSelect({
    multiple = false,
    mediaType,
    teacherId,
    onlyPreview,
    onPreview,
    onData,
    children,
    className,
    disabled,
    onStart,
    onCheck,
    onEnd,
    onSelectReady,
    onUploadError,
    progressHandler,
    accept,
    isValidate,
    validate,
    ...props
}: IFileSelectProps) {
    const onProgress = progressHandler;
    const input = useRef<HTMLInputElement>(null);
    const ua = umuWebview.UA_INFO;
    const inputAccept = typeof accept === 'string' ? expandAccept(accept) : expandMediaAccept(mediaType);

    function preview(file: File) {
        let previewUrl = URL.createObjectURL(file);

        if (onPreview) {
            onPreview(previewUrl, file);
        }
        return {
            file,
            previewUrl,
        };
    }

    async function encode({ file, previewUrl }: IPreviewResult): Promise<IEncodeResult> {
        const result: Blob = file;
        const fileExt = (file.name.split(/\./g).pop() || '').toLocaleLowerCase();
        /*  if (fileExt === 'png') {
             result = await compressPng(file)!;
         } else if ('jpg|jpeg'.indexOf(fileExt) > -1) {
             result = await compressJpeg(file)!;
         } */

        return Promise.resolve({
            blob: result,
            ext: fileExt,
            previewUrl,
            fileName: file.name,
            mediaType,
            teacherId,
            onProgress: (fileName: string, progress: IUploadProgress) => {
                if (onProgress instanceof Function) {
                    onProgress(fileName, progress);
                }
            },
        });
    }

    /**
     * @desc 手动的校验，因为某些移动端浏览器不支持 accept
     * @param files FileList
     */
    const validateExt = (files: FileList) => {
        if (!inputAccept) {
            return true;
        }
        const acceptArr = inputAccept.split(',');

        let flag = true;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const ext = (file.name.split(/\./g).pop() || '').toLocaleLowerCase();
            flag = acceptArr.indexOf(ext) !== -1;
            if (!flag) {
                break;
            }
        }
        return flag;
    };

    const extError = () => {
        Toast.show({
            message: lang('文件格式暂不支持，请重新选择'),
            callback() {
                if (onUploadError) {
                    onUploadError(new Error('File format is not legal'));
                }
            },
        });
    };

    async function processFiles(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files || !files.length) {
            return;
        }
        if (!validateExt(files)) {
            return extError();
        }
        if (validate && !validate(files)) {
            return;
        }
        const filelist = Array.from(files);
        if (onCheck) {
            const isOnCheck = await onCheck(filelist);
            if (!isOnCheck) {
                if (input && input.current) {
                    input.current.value = '';
                }
                return;
            }
        }
        if (onStart) {
            onStart();
        }

        let total = files.length;
        const previewList = Array.from(files).map(preview);
        const results = previewList.map(encode);
        onSelectReady && onSelectReady(previewList);
        if (onlyPreview) {
            event.target.value = '';
            return;
        }
        const payloadDatas: IEndData[] = [];
        if (onData) {
            results.map((result) => {
                result
                    .then(getUploadResult)
                    .then((payloadData) => {
                        total--;
                        onData(
                            payloadData.previewUrl,
                            payloadData.url,
                            payloadData.fileName,
                            String(payloadData.resourceId),
                        );
                        payloadDatas.push({
                            previewUrl: payloadData.previewUrl,
                            url: payloadData.url,
                            fileName: payloadData.fileName,
                            resourceId: String(payloadData.resourceId),
                        });
                        if (!total && onEnd) {
                            onEnd(payloadDatas);
                        }
                    })
                    .catch((e) => {
                        if (onUploadError) {
                            onUploadError(e);
                        }
                    });
            });
        }

        event.target.value = '';
    }

    const validateAccept = () => {
        if (isValidate) {
            return '';
        }
        return ua.isWeixinBrowser && ua.isAndroid ? '' : inputAccept;
    };
    const clsNames = classSet('file-select', className || '');
    return (
        <div className={clsNames} {...props}>
            <div className="file-select-content-wrap">{children}</div>
            <input
                type="file"
                ref={input}
                multiple={multiple}
                disabled={disabled}
                onChange={processFiles}
                accept={validateAccept()}
            />
        </div>
    );
}
